import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme } from "../theme";

// Theme context
const ThemeContext = createContext({
  mode: "light",
  toggleTheme: () => {},
  setMode: () => {},
});

// Hook to use theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
};

// Storage key
const THEME_STORAGE_KEY = "sortlybooks-theme-mode";

// Get system preference
const getSystemPreference = () => {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
};

// Get initial mode from storage or system
const getInitialMode = () => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") {
      return stored;
    }
    // Fall back to system preference
    return getSystemPreference();
  }
  return "light";
};

// Theme Provider component
export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(getInitialMode);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = (e) => {
      // Only update if user hasn't explicitly set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setModeState(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Persist mode to localStorage
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    
    // Also set data attribute on document for potential CSS usage
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  // Toggle between light and dark
  const toggleTheme = () => {
    setModeState((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Set specific mode
  const setMode = (newMode) => {
    if (newMode === "light" || newMode === "dark") {
      setModeState(newMode);
    } else if (newMode === "system") {
      localStorage.removeItem(THEME_STORAGE_KEY);
      setModeState(getSystemPreference());
    }
  };

  // Create theme based on current mode
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Context value
  const value = useMemo(
    () => ({
      mode,
      toggleTheme,
      setMode,
      isDark: mode === "dark",
      isLight: mode === "light",
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

